import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import get from "lodash/get";
import { postApi } from "../../redux/rest/restSlice";
import { LIST_COUPON_CODES } from "../../constants/actionTypes";
import {
  couponCodeTable,
  isCouponExpired,
} from "../../constants/couponCodeTables";
import { getNextSort, TABLE_PAGINATION } from "../../utilities/helper";
import DataTable from "../DataTable";
import SummaryStrip from "../SummaryStrip";
import AddEditCouponCode from "./addEditCouponCode";

const CouponCodes = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [modalInfo, setModalInfo] = useState({
    visible: false,
    data: null,
  });

  const dispatch = useDispatch();

  const fetchCouponCodes = useCallback(
    (search = "", sortBy = null) => {
      const term = search.trim();

      // Search matches either the coupon code or the name
      const filters = term ? { coupon_code: term, name: term } : {};

      setLoading(true);
      dispatch(
        postApi({
          endpoint: LIST_COUPON_CODES,
          payload: {
            filters,
            ...(sortBy ? { sortBy: sortBy.sortBy, sortDir: sortBy.sortDir } : {}),
          },
        })
      )
        .then(unwrapResult)
        .then((result) => {
          setLoading(false);
          setData(get(result, "data", []));
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    fetchCouponCodes();
  }, [fetchCouponCodes]);

  const onSearch = (value) => {
    setAppliedSearch(value);
    fetchCouponCodes(value, sort);
  };

  const clearSearch = () => {
    setSearchValue("");
    onSearch("");
  };

  // Paging is done in the table itself; only sorting reloads from the API
  const onTableChange = (_pagination, _filters, sorter, extra) => {
    if (get(extra, "action") !== "sort") return;

    const nextSort = getNextSort(sorter, sort);
    setSort(nextSort);
    fetchCouponCodes(appliedSearch, nextSort);
  };

  const openModal = (record = null) => {
    setModalInfo({ visible: true, data: record });
  };

  const closeModal = () => {
    setModalInfo({ visible: false, data: null });
  };

  const onSuccess = () => {
    closeModal();
    fetchCouponCodes(appliedSearch, sort);
  };

  const rows = data || [];
  const firstLoad = loading && !rows.length;

  const summary = rows.length
    ? [
        { label: "Total", value: rows.length },
        { label: "Active", value: rows.filter((r) => r.is_active).length, tone: "success" },
        { label: "Deleted", value: rows.filter((r) => !r.is_active).length, tone: "danger" },
        { label: "Expired", value: rows.filter(isCouponExpired).length, tone: "warning" },
      ]
    : [];

  return (
    <Spin spinning={loading && !firstLoad}>
      <div className="list-toolbar">
        <div className="list-toolbar__main">
          <Input.Search
            className="list-toolbar__search"
            placeholder="Search by coupon code or name"
            allowClear
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={onSearch}
          />
        </div>

        <div className="list-toolbar__actions">
          <Button
            type="primary"
            className="default-primary-btn"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Create Coupon Code
          </Button>
        </div>
      </div>

      {!firstLoad && <SummaryStrip items={summary} />}

      <DataTable
        skeleton={firstLoad}
        scrollX={1000}
        rowKey="id"
        className="mt-20"
        dataSource={rows}
        columns={couponCodeTable(openModal, sort)}
        onChange={onTableChange}
        pagination={TABLE_PAGINATION}
        emptyProps={{
          noun: "coupon codes",
          search: appliedSearch,
          onClearSearch: clearSearch,
        }}
      />

      <Modal
        width={640}
        title={get(modalInfo, "data") ? "Edit Coupon Code" : "Create Coupon Code"}
        destroyOnClose
        open={get(modalInfo, "visible")}
        onCancel={closeModal}
        footer={false}
      >
        <AddEditCouponCode
          modalInfo={modalInfo}
          setModalInfo={closeModal}
          onSuccess={onSuccess}
        />
      </Modal>
    </Spin>
  );
};

export default CouponCodes;
